import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Paper from "@material-ui/core/Paper";
import { schema } from "./schema";
import { useFormStyles } from "./styles";
import { UseMutationResult } from "@tanstack/react-query";
import TextField from "@material-ui/core/TextField";
import WatchLater from "@material-ui/icons/WatchLater";
import ServiceField from "./components/service-field";
import { Box, Button, Grid, Typography } from "@material-ui/core";
import { AttendanceLocalDepartmentQueue } from "../../../../../../../../../../../../../@types/attendance-local-department-queue";
import { AttendanceLocalDepartmentQueueServiceTimeComplement } from "../../../../../../../../../../../../../@types/attendance-local-department-queue-service-time-complement";
import { AttendanceLocalDepartmentQueueServiceTimeComplementCreateData } from "../../../../../../../../../../../../../services/api-tansquery/attendance-local/department/queue/service-time/complement/create";
import { AttendanceLocal } from "../../../../../../../../../../../../../@types/attendance-local";
import { handleNumberChange } from "../../../../../../../../../../../../../utils/handleNumberChange";
import { AttendanceLocalDepartment } from "../../../../../../../../../../../../../@types/attendance-local-department";

type FormProps = {
    unidade: AttendanceLocal["slug_unidade"];
    setor_unidade: AttendanceLocalDepartment["id"];
    fila_id: AttendanceLocalDepartmentQueue["id"];
    serviceTimeComplement?: AttendanceLocalDepartmentQueueServiceTimeComplement;
    mutation?: UseMutationResult<
        AttendanceLocalDepartmentQueueServiceTimeComplement,
        unknown,
        AttendanceLocalDepartmentQueueServiceTimeComplementCreateData,
        unknown
    >
};

export default function Form({ unidade, setor_unidade, fila_id, serviceTimeComplement, mutation }: FormProps) {
    const numberFieldProps = { min: 0, max: 100 };
    const history = useHistory();
    const { classes } = useFormStyles();
    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {
            fila: fila_id.toString(),
            servico: serviceTimeComplement?.servico.id.toString() || "",
            tempo: "",
        }
    });

    useEffect(() => {
        if (serviceTimeComplement) {
            form.reset({
                fila: fila_id.toString(),
                servico: serviceTimeComplement.servico.id.toString(),
                tempo: serviceTimeComplement.tempo?.toString() || "",
            });
        }
    }, [form, serviceTimeComplement, fila_id]);

    async function handleSubmit(data: z.infer<typeof schema>) {
        if (mutation.isLoading) {
            return;
        }

        await mutation.mutateAsync({
            fila: fila_id,
            servico: Number(data.servico),
            tempo: Number(data.tempo),
        })
    }

    function preventNonNumericInput(event: React.KeyboardEvent<HTMLInputElement>) {
        if (!/[0-9]/.test(event.key) && event.key !== 'Backspace' && event.key !== 'Delete' && event.key !== 'Tab' && event.key !== 'Enter') {
            event.preventDefault();
        }
    }

    useEffect(() => {
        if (mutation.isSuccess) {
            history.goBack();
        }
    }, [mutation.isSuccess, history]);

    return (
        <Paper
            elevation={3}
            style={{ padding: 20 }}
            component="form"
            onSubmit={form.handleSubmit(handleSubmit)}
        >
            <Grid container spacing={2}>
                <Grid item xs={12} component={Typography} className={classes.title}>
                    Gerenciar Configuração de Tempo (Serviço Complementar)
                </Grid>

                <Grid item xs={12}>
                    <Controller
                        name="servico"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <ServiceField
                                field={field}
                                fieldState={fieldState}
                                unidade={unidade}
                                setor_unidade={setor_unidade}
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Controller
                        name="tempo"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                required
                                fullWidth
                                type="number"
                                margin="normal"
                                variant="outlined"
                                label="Tempo de Atendimento"
                                error={!!fieldState.error}
                                helperText={
                                    fieldState.error ? fieldState.error.message : "Informe o tempo de duração do atendimento"
                                }
                                inputProps={{
                                    ...numberFieldProps,
                                    className: classes.textField,
                                }}
                                InputProps={{
                                    required: false,
                                    endAdornment: <WatchLater htmlColor="#C4C4C4" />
                                }}
                                onKeyPress={preventNonNumericInput}
                                onChange={(e) => {
                                    let value = handleNumberChange(
                                        numberFieldProps.min,
                                        numberFieldProps.max,
                                        e.target.value
                                    );
                                    field.onChange(value.toString());
                                }}
                            />
                        )}
                    />
                </Grid>
            </Grid>
            <Box className={classes.footer}>
                <Button
                    className={classes.button}
                    onClick={() => history.goBack()}
                    disabled={mutation.isLoading}
                >
                    Cancelar
                </Button>
                <Button
                    type="submit"
                    className={classes.saveButton}
                    disabled={mutation.isLoading}
                >
                    {mutation.isLoading ? 'Salvando...' : 'Salvar'}
                </Button>
            </Box>
        </Paper>
    );
}
